import GuideLayout from './GuideLayout';

export default function GuidePrezziAirbnbBooking() {
  return (
    <GuideLayout
      slug="prezzi-airbnb-booking"
      title="Come fare i prezzi su Airbnb e Booking: framework completo 2026"
      breadcrumbLabel="Prezzi Airbnb e Booking"
      description="Il framework che usano i top host italiani per fissare i prezzi su Airbnb e Booking: base price, RevPAR, ADR, fattori stagionali, eventi locali, lead time, length-of-stay. Esempi numerici e percentuali di rincaro per evento."
      readingTime={14}
      faqs={faqs}
      howTo={howTo}
    >
      <h2>Perché il prezzo "giusto" non esiste</h2>
      <p>
        Il prezzo di una notte non è un numero fisso. È <strong>una funzione</strong> che dipende da almeno 8 variabili: stagione, giorno della settimana, eventi locali nel raggio di 30 km, livello di occupazione del tuo immobile, prezzo dei competitor, lead time (giorni alla prenotazione), durata del soggiorno, qualità del tuo annuncio.
      </p>
      <p>
        I top host italiani — quelli che mantengono <strong>occupazione &gt; 75%</strong> e <strong>ADR &gt; 100€</strong> — usano un <strong>algoritmo mentale</strong> (o automatico tramite tool come PriceLabs, Wheelhouse, Beyond) per aggiornare i prezzi <strong>almeno una volta a settimana</strong>. Vediamo come.
      </p>

      <h2>I 3 KPI fondamentali</h2>
      <h3>1. ADR — Average Daily Rate</h3>
      <p>
        Il <strong>prezzo medio per notte effettivamente venduta</strong>. Si calcola dividendo il fatturato per il numero di notti vendute.
      </p>
      <p>
        <code>ADR = Fatturato / Notti vendute</code>
      </p>
      <p>Esempio: 30 notti vendute a febbraio con incasso 2.700€ → ADR = 90€/notte.</p>

      <h3>2. Occupancy Rate</h3>
      <p>La <strong>percentuale di notti vendute</strong> sul totale di notti disponibili nel periodo.</p>
      <p><code>Occupancy = Notti vendute / Notti disponibili × 100</code></p>
      <p>Esempio: 30 notti vendute / 28 disponibili a febbraio → 78%.</p>

      <h3>3. RevPAR — Revenue Per Available Room</h3>
      <p>Il <strong>KPI più importante</strong>: combina prezzo e occupazione, mostra quanto rende ogni notte disponibile.</p>
      <p><code>RevPAR = ADR × Occupancy = Fatturato / Notti disponibili</code></p>
      <p>Esempio: 90€ × 0,78 = <strong>70€ RevPAR</strong>. Significa che ogni notte disponibile genera 70€ in media.</p>

      <div className="callout callout-info">
        Il <strong>RevPAR è la stella polare</strong>. Un ADR alto ma occupazione bassa è peggio di un ADR medio con occupazione piena. Esempio: 150€ × 40% = 60€ RevPAR vs 90€ × 75% = 67€ RevPAR.
      </div>

      <h2>Il framework di pricing in 6 step</h2>

      <h3>Step 1 — Base price</h3>
      <p>
        Il "base price" è il prezzo che applichi nel <strong>periodo di domanda media</strong>, in <strong>bassa stagione</strong>, in <strong>giorno feriale</strong>. Per calcolarlo:
      </p>
      <ol>
        <li>Identifica <strong>10 competitor diretti</strong> nel raggio di 1 km, con stessa tipologia (mono/bilo/casa), capacità simile e rating ≥4,5.</li>
        <li>Guarda i loro prezzi per una notte feriale di novembre (o periodo morto della tua zona).</li>
        <li>Fai la <strong>mediana</strong>: scarta il 20% più basso e il 20% più alto, prendi il valore centrale.</li>
        <li>Se hai un USP (vista mare, parcheggio, terrazzo) puoi puntare al +10/15%. Se hai handicap (no ascensore al 4° piano), puntare al -10%.</li>
      </ol>

      <h3>Step 2 — Fattore stagionale</h3>
      <p>Moltiplica il base price per il coefficiente stagionale tipico della tua zona:</p>
      <table>
        <thead>
          <tr><th>Stagione</th><th>Coefficiente medio Italia</th></tr>
        </thead>
        <tbody>
          <tr><td>Bassa (gen, feb, nov)</td><td>×1,00 (base)</td></tr>
          <tr><td>Media (mar, apr, ott)</td><td>×1,20 - 1,40</td></tr>
          <tr><td>Alta (mag, giu, set)</td><td>×1,50 - 1,80</td></tr>
          <tr><td>Altissima (lug, ago, festività)</td><td>×2,00 - 3,00</td></tr>
        </tbody>
      </table>
      <p>Esempio: base 70€ × 2,2 (agosto in Liguria costiera) = 154€.</p>

      <h3>Step 3 — Fattore giorno della settimana</h3>
      <ul>
        <li>Lun-Gio: ×1,00 (base)</li>
        <li>Ven, Dom: ×1,15</li>
        <li>Sab: ×1,30 - 1,40</li>
      </ul>
      <p>
        Eccezione: <strong>città business</strong> (Milano, Torino, Bologna in zone fieristiche). Lì il pattern si <strong>inverte</strong>: lun-mer alti per i viaggiatori business, weekend in calo.
      </p>

      <h3>Step 4 — Eventi locali (il moltiplicatore vero)</h3>
      <p>
        Gli eventi locali sono <strong>la leva più potente</strong>. Vediamo i mega-eventi 2026/2027 con percentuali di rincaro tipiche nelle aree limitrofe:
      </p>
      <table>
        <thead>
          <tr><th>Evento</th><th>Quando</th><th>Rincaro tipico</th></tr>
        </thead>
        <tbody>
          <tr><td>Festival di Sanremo</td><td>Feb 2027</td><td>×3 - ×5 entro 10 km</td></tr>
          <tr><td>America's Cup Napoli</td><td>Estate 2027</td><td>×2 - ×4</td></tr>
          <tr><td>Salone del Mobile MI</td><td>Apr 2026/27</td><td>×2,5 - ×4 in città</td></tr>
          <tr><td>Vasco Rossi tour stadi</td><td>Giugno '26</td><td>×1,8 - ×3 nelle città tour</td></tr>
          <tr><td>F1 Imola</td><td>Maggio</td><td>×2 - ×3 entro 30 km</td></tr>
          <tr><td>F1 Monza</td><td>Settembre</td><td>×2 - ×3 entro 30 km</td></tr>
          <tr><td>MotoGP Mugello</td><td>Maggio/Giu</td><td>×2 - ×2,5</td></tr>
          <tr><td>Carnevale Venezia</td><td>Feb</td><td>×2 - ×3</td></tr>
          <tr><td>Pitti Uomo FI</td><td>Gen, Giu</td><td>×1,5 - ×2,5</td></tr>
          <tr><td>Mille Miglia</td><td>Giugno</td><td>×1,5 - ×2,2</td></tr>
        </tbody>
      </table>
      <p>
        Locazione Turistica integra <strong>419 eventi italiani 2026/2027</strong> nel calendario host con alert automatici: 60 giorni prima dell'evento ti suggeriamo il rincaro tipico per la tua zona.
      </p>

      <h3>Step 5 — Lead time</h3>
      <p>
        Più la notte si avvicina senza che sia stata venduta, più conviene <strong>scendere</strong> di prezzo. Strategia tipica:
      </p>
      <ul>
        <li>Da 60 a 30 giorni prima: prezzo pieno.</li>
        <li>Da 30 a 14 giorni: -5/10% se occupazione &lt; 50%.</li>
        <li>Da 14 a 7 giorni: -15/20%.</li>
        <li>Last minute (3-7 giorni): -25/35%, ma valuta se la notte vuota costa meno (no costi pulizia/utenze).</li>
      </ul>
      <p>
        <strong>Eccezione</strong>: in pieno periodo evento, <strong>sali</strong> negli ultimi 10 giorni perché la domanda inelastica accetta prezzi crescenti.
      </p>

      <h3>Step 6 — Length-of-stay discount</h3>
      <p>
        Soggiorni più lunghi = meno turn-over, meno costi pulizia/check-in. Sconti tipici Airbnb:
      </p>
      <ul>
        <li><strong>Sconto settimanale</strong> (7+ notti): 5-10%.</li>
        <li><strong>Sconto mensile</strong> (28+ notti): 15-25%. Attenzione: sopra i 30 giorni cambia regime fiscale!</li>
      </ul>

      <h2>Errori da evitare</h2>
      <ul>
        <li><strong>Prezzo fisso tutto l'anno</strong>: lasci sul tavolo il 20-40% del fatturato annuale. Cardinal sin.</li>
        <li><strong>Copiare il primo competitor</strong> senza guardare 10. Uno potrebbe essere a 60€ perché è in svalutazione su Airbnb (basso rating).</li>
        <li><strong>Non considerare la fee Airbnb/Booking</strong>: il tuo prezzo "pulito" è già al netto delle commissioni; il guest vede un prezzo finale +14% (Airbnb commission split) o +18% (Booking).</li>
        <li><strong>Ignorare il cancellation rate</strong>: se metti prezzo troppo alto e arrivi a 80% occupazione ma con 15% di cancellazioni, il RevPAR reale è molto più basso.</li>
        <li><strong>Sconti permanenti</strong>: meglio prezzo base alto + sconti tattici. Lo sconto permanente abbassa il valore percepito.</li>
      </ul>

      <h2>Strumenti per pricing dinamico</h2>
      <p>
        Per chi gestisce 3+ immobili e vuole automatizzare:
      </p>
      <ul>
        <li><strong>PriceLabs</strong> — ~25$/mese per immobile, integrazione diretta Airbnb/Booking.</li>
        <li><strong>Wheelhouse</strong> — fascia simile, UI più semplice.</li>
        <li><strong>Beyond Pricing</strong> — più orientato property manager, modello commissionale 1% del fatturato.</li>
        <li><strong>Airbnb Smart Pricing</strong> — gratis ma molto aggressivo al ribasso. Solo come ultima spiaggia.</li>
      </ul>

      <h2>Caso studio: appartamento Genova 60mq</h2>
      <p>Bilocale, vista parziale mare, 4 posti letto, rating 4,87 su Airbnb.</p>
      <ul>
        <li>Base price (novembre, feriale): <strong>65€</strong></li>
        <li>Stagione media (aprile): 65 × 1,3 = <strong>84€</strong></li>
        <li>Stagione alta (luglio, feriale): 65 × 1,6 = <strong>104€</strong></li>
        <li>Stagione alta (luglio, sabato): 65 × 1,6 × 1,35 = <strong>140€</strong></li>
        <li>Salone Nautico (settembre, 6 giorni): 65 × 1,5 × 2,2 = <strong>215€</strong></li>
      </ul>
      <p>
        Con questa logica nel 2024 RevPAR annuale ≈ 78€/notte (sopra la media italiana di 55€/notte per la fascia).
      </p>

      <blockquote>
        Il prezzo non è una decisione, è una conversazione continua con il mercato. Aggiornalo o lo aggiorneranno (in peggio) i tuoi competitor.
      </blockquote>
    </GuideLayout>
  );
}

const howTo = {
  name: 'Come fissare i prezzi su Airbnb e Booking in modo dinamico',
  steps: [
    { name: 'Calcola il base price', text: 'Confronta 10 competitor diretti nella tua zona, in periodo morto e giorno feriale. Fai la mediana scartando i 2 valori estremi sopra e sotto.' },
    { name: 'Applica il coefficiente stagionale', text: 'Moltiplica il base price per il fattore stagionale tipico della tua area: 1,0 in bassa, 1,2-1,4 in media, 1,5-1,8 in alta, 2,0-3,0 in altissima.' },
    { name: 'Aggiusta per giorno della settimana', text: 'Aggiungi +15% nei venerdì e domeniche, +30/40% nei sabati. Per le città business inverti il pattern: lun-mer più alti, weekend più bassi.' },
    { name: 'Verifica gli eventi locali', text: 'Controlla nel raggio di 30 km eventi nei prossimi 90 giorni (concerti, fiere, festival, gare sportive). Applica moltiplicatori da 1,5 a 5 a seconda dell\'evento.' },
    { name: 'Adatta per lead time e durata', text: 'Negli ultimi 14 giorni applica sconti progressivi 5-25% se l\'occupazione è bassa. Per soggiorni 7+ notti sconto 5-10%; per 28+ notti sconto 15-25%.' },
    { name: 'Misura il RevPAR', text: 'Ogni 30 giorni calcola ADR × Occupancy e confrontalo con il mese precedente. Se è in calo, rivedi base price o aumenta visibility dell\'annuncio.' },
  ],
};

const faqs = [
  { q: 'Qual è il KPI più importante per gli affitti brevi?', a: 'Il RevPAR (Revenue Per Available Room): è il prodotto fra ADR (prezzo medio per notte) e Occupancy (tasso di occupazione). Rappresenta quanto rende ogni notte disponibile e va monitorato mese su mese. Un ADR alto con occupazione bassa è peggio di un ADR medio con occupazione piena.' },
  { q: 'Quanto deve costare la mia notte base?', a: 'Calcolala dalla mediana di 10 competitor diretti (stessa tipologia, capacità, rating ≥4,5) in periodo morto e giorno feriale. Aggiusta +10/15% se hai un USP forte (vista mare, parcheggio), -10% se hai handicap.' },
  { q: 'Quanto alzare per i grandi eventi?', a: 'Dipende dall\'evento e dalla distanza. Per il Festival di Sanremo entro 10 km le tariffe vanno da ×3 a ×5. Per la F1 di Monza entro 30 km da ×2 a ×3. Per il Salone del Mobile a Milano da ×2,5 a ×4. La regola: più l\'evento è "obbligatorio per partecipare" (motorsport, fiere B2B), più il rincaro regge.' },
  { q: 'Vale la pena usare un software di pricing dinamico?', a: 'Sopra i 3 immobili sì, sotto è marginale. Tool come PriceLabs (25$/mese/immobile) o Wheelhouse fanno un lavoro mediamente +5-12% sul RevPAR rispetto al manuale settimanale. Da soli su un singolo appartamento, una buona logica manuale aggiornata ogni 7 giorni rende quasi quanto.' },
  { q: 'Lo Smart Pricing di Airbnb funziona?', a: 'Funziona ma è aggressivo al ribasso: Airbnb preferisce occupazione 100% a prezzo basso piuttosto che 75% a prezzo alto. Va usato solo come ultima spiaggia quando hai immobili nuovi senza recensioni, oppure dopo modifiche al nucleo (cambio foto, descrizione) per "riprendere visibilità".' },
  { q: 'Come gestire il prezzo last minute?', a: 'Da 14 giorni in giù applica sconti progressivi 5-25% se l\'occupazione del periodo è sotto il 50%. Eccezione: in pieno periodo evento (Sanremo, F1, Vasco), invece di scendere SALI negli ultimi 10 giorni — la domanda inelastica accetta prezzi crescenti.' },
  { q: 'Quanto perde un host che tiene il prezzo fisso tutto l\'anno?', a: 'Tra il 20% e il 40% del fatturato annuo potenziale, a seconda della località. In aree con forte stagionalità (montagna, mare) il prezzo fisso è il peggior errore strategico possibile.' },
];
